/**
 * extractionTimeout — helper pur (Chunk 9.M.3 / D22).
 *
 * Pourquoi un module séparé
 * --------------------------
 * Avant 9.M.3, `ExtractionPanel.tsx` codait en dur `TIMEOUT_MS = 5 * 60 * 1000`
 * (5 min). Pour les PDF lourds (>50 pages, gros tableaux), 5 min ne suffit
 * parfois pas et l'utilisateur voit `timed_out` alors que le job MCP est
 * encore en cours côté backend.
 *
 * 9.M.3 / D22 — on rend le timeout configurable côté UI **uniquement**
 * (zéro backend, zéro contrat API), persisté en `localStorage` global
 * (clé `kollectivia.extraction.timeoutMs`), et borné aux 4 paliers fixes
 * décidés par PO :
 *
 *   60s · 120s · 300s (défaut) · 600s
 *
 * Doctrine 9.M.x (anti-RTL/jsdom — D21 reste fermée) : ce module est
 * **pur Node** (zéro import React, DOM, Supabase, fetch). Les seules
 * dépendances runtime sont `localStorage` (window) — accédées via guards
 * `typeof window !== "undefined"` pour rester testables en Node sans
 * jsdom. La config Vitest existante (`environment: "node"`,
 * include `src` glob `*.test.ts`) capte ce fichier sans modification de
 * `vitest.config.ts`.
 *
 * Anti-régression
 * ----------------
 * Si `localStorage` est indisponible (SSR, navigateur privé strict) ou
 * si la valeur est invalide / hors paliers / NaN, on tombe sur le
 * `DEFAULT_TIMEOUT_MS` (300_000). Aucun throw — le panneau d'extraction
 * doit toujours pouvoir démarrer un job.
 */

/** Clé localStorage globale (par browser / utilisateur, pas par submission). */
export const TIMEOUT_STORAGE_KEY = "kollectivia.extraction.timeoutMs";

/**
 * Paliers acceptés. Toute valeur hors de cette liste retombe sur
 * `DEFAULT_TIMEOUT_MS` au read.
 *
 * Décision PO (9.M.3, Q-B) : 4 paliers exacts, pas de slider continu, pour
 * éviter les saisies aberrantes (1ms, 24h, etc.) et garder une UI simple
 * (4 boutons radio dans le bandeau timed_out).
 */
export const ALLOWED_TIMEOUT_VALUES_MS = [
  60_000, // 60s — extractions courtes (1-3 pages)
  120_000, // 120s — extractions moyennes
  300_000, // 300s = 5 min — défaut (parité comportement pré-9.M.3)
  600_000, // 600s = 10 min — gros PDF, gros tableaux
] as const;

export type AllowedTimeoutMs = (typeof ALLOWED_TIMEOUT_VALUES_MS)[number];

/** Valeur par défaut — alignée comportement pré-9.M.3 (5 min). */
export const DEFAULT_TIMEOUT_MS: AllowedTimeoutMs = 300_000;

/**
 * Type guard runtime : la valeur est-elle un palier accepté ?
 *
 * Utilisé en interne par `getExtractionTimeoutMs` mais exporté pour les
 * tests purs et pour le composant (UI radio qui veut savoir si le palier
 * stocké correspond à un bouton actif).
 */
export function isAllowedTimeoutMs(
  value: unknown,
): value is AllowedTimeoutMs {
  if (typeof value !== "number") return false;
  if (!Number.isFinite(value)) return false;
  return (ALLOWED_TIMEOUT_VALUES_MS as readonly number[]).includes(value);
}

/**
 * Lit la valeur courante depuis `localStorage`, ou rend
 * `DEFAULT_TIMEOUT_MS` si absente / invalide / palier non autorisé.
 *
 * Garantit toujours de retourner un `AllowedTimeoutMs` — jamais NaN,
 * jamais Infinity, jamais hors paliers. Aucun throw.
 *
 * @returns timeout effectif en ms (toujours dans `ALLOWED_TIMEOUT_VALUES_MS`)
 */
export function getExtractionTimeoutMs(): AllowedTimeoutMs {
  // Guard SSR / environnement Node — `window` peut ne pas exister.
  if (typeof window === "undefined") return DEFAULT_TIMEOUT_MS;
  // Guard localStorage — désactivé en navigateur privé strict / iframe sandbox.
  if (typeof window.localStorage === "undefined") return DEFAULT_TIMEOUT_MS;

  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(TIMEOUT_STORAGE_KEY);
  } catch {
    // Accès localStorage peut throw (cookies tiers bloqués, etc.) — fallback safe.
    return DEFAULT_TIMEOUT_MS;
  }

  if (raw === null || raw === "") return DEFAULT_TIMEOUT_MS;

  const parsed = Number(raw);
  if (!isAllowedTimeoutMs(parsed)) return DEFAULT_TIMEOUT_MS;
  return parsed;
}

/**
 * Persiste une nouvelle valeur de timeout. Refuse silencieusement si la
 * valeur n'est pas un palier accepté (le composant doit appeler avec un
 * palier valide — cette protection est une ceinture+bretelles).
 *
 * @param ms palier en ms (doit appartenir à `ALLOWED_TIMEOUT_VALUES_MS`)
 * @returns `true` si la valeur a été persistée, `false` sinon
 */
export function setExtractionTimeoutMs(ms: number): boolean {
  if (!isAllowedTimeoutMs(ms)) return false;
  if (typeof window === "undefined") return false;
  if (typeof window.localStorage === "undefined") return false;
  try {
    window.localStorage.setItem(TIMEOUT_STORAGE_KEY, String(ms));
    return true;
  } catch {
    // QuotaExceeded, accès refusé, etc. — on n'interrompt pas l'UI.
    return false;
  }
}
